import { createSlice } from "@reduxjs/toolkit";
import {
  Timestamp,
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  writeBatch,
  increment,
} from "firebase/firestore";
import { firestore } from "contexts/FirebaseContext";
import { dispatch } from "store";
import { openSnackbar } from "./snackbar";

// Helper array for month names
const monthNames = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
];

const formatDate = (date) => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${year}-${month}-${day}`;
};

const formatListUpdateDate = (date) => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
};

const initialState = {
  lists: [],
  listContacts: [],
  list: null,
  error: null,
};

const list = createSlice({
  name: "list",
  initialState,
  reducers: {
    hasError(state, action) {
      state.error = action.payload.error;
    },
    getAll(state, action) {
      state.lists = action.payload;
    },
    getAllContacts(state, action) {
      state.listContacts = action.payload;
    },
  },
});

export default list.reducer;
export const { getAll } = list.actions;

export const subscribeToLists = () => {
  const unsubscribe = onSnapshot(collection(firestore, "lists"), (snapshot) => {
    const data = snapshot.docs.map((doc) => doc.data());
    dispatch(list.actions.getAll(data));
  });
  return unsubscribe;
};

export const subscribeToListContacts = (listId, limitFilter) => {
  const unsubscribe = onSnapshot(
    query(collection(firestore, `lists/${listId}/contacts`), limit(limitFilter)),
    (snapshot) => {
      const data = snapshot.docs.map((doc) => doc.data());
      dispatch(list.actions.getAllContacts(data));
    }
  );
  return unsubscribe;
};

export async function getListCount(listId) {
  try {
    const colRef = collection(firestore, "lists/" + listId + "/contacts");
    const snapshot = await getCountFromServer(colRef);
    return snapshot.data().count;
  } catch (error) {
    console.error("Error getting document count:", error);
  }
}

export async function createList(list, invoiceNumber) {
  try {
    const id = "INV-1-" + invoiceNumber;
    const docref = doc(collection(firestore, "lists"), id);
    list.id = id;
    list.statusUpdated = new Date().toLocaleDateString("en-GB").replaceAll("/", ".");
    await setDoc(docref, list);
    let newInvoiceNumber = parseInt(invoiceNumber) + 1;
    updateDoc(doc(firestore, "data/invoice"), {
      invoiceNumber: newInvoiceNumber.toString(),
    });
  } catch (error) {
    console.error(error);
  }
}

export async function createListContact(listId, contact) {
  try {
    const docref = doc(collection(firestore, "lists/" + listId + "/contacts"), contact.id);
    await setDoc(docref, contact);
  } catch (error) {
    console.error(error);
  }
}

export async function createListContacts(listId, contacts) {
  const collected_business_ids = [];

  try {
    const chunkSize = 500;
    const batchCommits = [];
    for (let i = 0; i < contacts.length; i += chunkSize) {
      const batch = writeBatch(firestore);
      const chunk = contacts.slice(i, i + chunkSize);
      chunk.forEach((contact) => {
        const colRef = doc(collection(firestore, `lists/${listId}/contacts`), contact.id);
        batch.set(colRef, contact);
        collected_business_ids.push(contact.id);
      });
      batchCommits.push(batch.commit());
    }
    await Promise.all(batchCommits);

    let existingCollectedIds = [];
    const collectedCollectionRef = collection(firestore, `lists/${listId}/collected`);
    const collectedDocsSnap = await getDocs(collectedCollectionRef);
    collectedDocsSnap.forEach((docSnap) => {
      const data = docSnap.data();
      if (data && Array.isArray(data.collected)) existingCollectedIds.push(...data.collected);
    });
    collected_business_ids.push(...existingCollectedIds);

    const uniqueIds = Array.from(new Set(collected_business_ids)).sort();
    let highestSuffix = -1;
    let highestDocId = "collected_0";
    collectedDocsSnap.forEach((docSnap) => {
      const docId = docSnap.id;
      const match = docId.match(/^collected_(\d+)$/);
      if (match) {
        const suffix = parseInt(match[1], 10);
        if (suffix > highestSuffix) {
          highestSuffix = suffix;
          highestDocId = docId;
        }
      }
    });
    const collectedDocRef = doc(firestore, `lists/${listId}/collected/${highestDocId}`);
    await updateDoc(collectedDocRef, { collected: uniqueIds });
    dispatch(
      openSnackbar({
        open: true,
        message: "Success",
        variant: "alert",
        alert: { color: "success" },
        close: true,
      })
    );
  } catch (error) {
    console.error(error);
  }
}

export async function updateList(list) {
  try {
    const docRef = doc(collection(firestore, "lists"), list.id);
    list.last_updated = Timestamp.fromDate(new Date());
    await updateDoc(docRef, list);
  } catch (error) {
    console.error(error);
  }
}

export async function updateListContact(listId, contact) {
  try {
    const docRef = doc(collection(firestore, "lists/" + listId + "/contacts"), contact.id);
    await updateDoc(docRef, contact);
  } catch (error) {
    console.error(error);
  }
}

export async function deleteList(id) {
  try {
    const docRef = doc(collection(firestore, "lists"), id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error(error);
  }
}

export async function deleteListContact(listId, id) {
  try {
    const docRef = doc(collection(firestore, "lists/" + listId + "/contacts"), id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error(error);
  }
}

/**
 * UPDATED: Moves a contact from the current list to a target list.
 * Skips KPI increment if targetListIdOverride is provided.
 */
export async function moveContactToList(coldListId, contact, targetListIdOverride = null) {
  const coldListDocRef = doc(firestore, "lists", coldListId);
  const coldListSnap = await getDoc(coldListDocRef);

  if (!coldListSnap.exists()) return;

  const listData = coldListSnap.data();
  const finalTargetId = targetListIdOverride || listData?.warm_list_id;

  if (!finalTargetId) return;

  const currentDate = new Date();
  const formattedContactDate = formatDate(currentDate);
  const formattedListUpdateString = formatListUpdateDate(currentDate);
  const currentTimestamp = Timestamp.fromDate(currentDate);
  const batch = writeBatch(firestore);

  try {
    const newContact = {
      ...contact,
      movedAt: currentTimestamp,
      last_contact: formattedContactDate,
      next_contact: "N/A",
    };

    const targetDocRef = doc(collection(firestore, `lists/${finalTargetId}/contacts`), contact.id);
    batch.set(targetDocRef, newContact);

    const sourceDocRef = doc(collection(firestore, `lists/${coldListId}/contacts`), contact.id);
    batch.delete(sourceDocRef);

    const warmListDocRef = doc(firestore, "lists", finalTargetId);
    batch.update(warmListDocRef, {
      last_updated_string: formattedListUpdateString,
      last_updated: currentTimestamp,
    });

    // ONLY INCREMENT KPI IF NO OVERRIDE IS PROVIDED (Meaning it went to the Warm List)
    if (!targetListIdOverride) {
      const year = currentDate.getFullYear();
      const monthName = monthNames[currentDate.getMonth()];

      // 1. Path updated to 'outreach' as per your new structure
      const outreachDocRef = doc(firestore, `data/outreach/data_${year}/${monthName}`);

      // 2. Use the date key (YYYY-MM-DD) to match the Outreach chart logic
      const dateKey = formatDate(currentDate);

      batch.set(
        outreachDocRef,
        {
          [dateKey]: {
            // Incrementing 'kpi' specifically within that date object
            kpi: increment(1),
          },
        },
        { merge: true }
      );
    }

    await batch.commit();
    dispatch(
      openSnackbar({
        open: true,
        message: !targetListIdOverride ? "Boom! 🎉" : "Done",
        variant: "alert",
        alert: { color: "success" },
        close: true,
      })
    );
  } catch (error) {
    console.error(error);
  }
}
